import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Button, Select, Checkbox, message } from 'antd';
import { useForm } from 'react-hook-form';
import { loader } from 'graphql.macro';
import { useMutation, useQuery } from '@apollo/client';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { OptionProps } from 'antd/es/select';
import {
  MdsaTaskParamsMetadataColumnsQuery,
  MdsaTaskParamsMetadataColumnsQueryVariables,
  UpdateAndStartMdsaTaskMutation,
  UpdateAndStartMdsaTaskMutationVariables,
  MetadataColumn,
  MdsaInParam,
  Enum_Mdsa_Task_Key_Enum,
} from '../graphql/graphql-types';
import EcFormItem from '../components/EcFormItem';
import AppLayout from '../components/AppLayout';
import {
  distanceMatrixOptions,
  filterSelectOptions,
  mdsaTasksInitialValues,
} from '../utils/globals';
import { logger } from '../utils/helpers';
import styles from './ScreenStyles.module.scss';

const mdsaTaskParamsMetadataColumnsQuery = loader(
  '../graphql/queries/mdsaTaskParamsMetadataColumnsQuery.graphql',
);
const updateAndStartMdsaTaskMutation = loader(
  '../graphql/mutations/updateAndStartMdsaTaskMutation.graphql',
);

/* Variable to store updated distanceMatrixOptions with key label for CheckBoxGroup field */
const distanceMatrixOptionsForCheckbox = distanceMatrixOptions.map((item) => {
  return { label: item.name, value: item.value };
});

const MdsaBetaDiversity: React.FC = () => {
  /* Extracting id and task id from url params */
  const { id, taskId } = useParams();

  const navigate = useNavigate();

  /* State to store started_at for updateAndStartMdsaTask */
  const [started_at] = useState(new Date());

  /* State to store start analysis buttons loading */
  const [startAnalysisBtnLoading, setStartAnalysisBtnLoading] = useState(false);

  /* useForm declaration with default values  */
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { handleSubmit, errors, control, setValue } = useForm<{
    distanceMatrices: Array<string>;
    metadataColumns: Array<string>;
  }>({
    resolver: yupResolver(
      yup.object().shape({
        distanceMatrices: yup.array().min(1, 'Please select at least one matrix and try again'),
        metadataColumns: yup
          .array()
          .min(1, 'Please select at least one metadata column and try again'),
      }),
    ),
    mode: 'onSubmit',
    defaultValues: {
      distanceMatrices: [],
      metadataColumns: [],
    },
  });

  /* Query to fetch mdsa data for mdsa group table bar plot */
  const { data, loading: mdsaTaskParamsMetadataColumnsLoading, error: queryError } = useQuery<
    MdsaTaskParamsMetadataColumnsQuery,
    MdsaTaskParamsMetadataColumnsQueryVariables
  >(mdsaTaskParamsMetadataColumnsQuery, {
    variables: {
      taskId: parseInt(taskId, 10),
    },
    fetchPolicy: 'network-only',
  });

  /* Mutation to update and start mdsa task */
  const [updateAndStartMdsaTask] = useMutation<
    UpdateAndStartMdsaTaskMutation,
    UpdateAndStartMdsaTaskMutationVariables
  >(updateAndStartMdsaTaskMutation);

  /* Function to update and start mdsa task on click start analysis button */
  const handleUpdateAndStartMdsaTask = (inParamsData: Array<MdsaInParam>) => {
    setStartAnalysisBtnLoading(true);
    updateAndStartMdsaTask({
      variables: {
        taskId: parseInt(taskId, 10),
        taskUpdateInput: {
          started_at,
          in_params: inParamsData,
        },
      },
    })
      .then((res) => {
        const updatedMdsaTaskId = res.data?.update_mdsa_task_by_pk?.id;
        if (updatedMdsaTaskId) {
          setStartAnalysisBtnLoading(false);
          navigate(`/mdsa/${id}`);
        }
      })
      .catch((err) => {
        setStartAnalysisBtnLoading(false);
        logger(err);
      });
  };

  /* Mdsa task in params data */
  const mdsaTaskInParams = data?.task?.in_params as Array<MdsaInParam>;

  /* Mdsa metadata columns data */
  const mdsaMetadataColumnsData = data?.task?.mdsa.metadata_columns as MetadataColumn[];

  // checks if default value is present if present sets it accordingly
  useEffect(() => {
    if (Array.isArray(mdsaTaskInParams) && mdsaTaskInParams.length > 0) {
      mdsaTaskInParams.forEach((item) => {
        if (item.key === 'distanceMatrices' && item.rawValue) {
          setValue('distanceMatrices', item.rawValue as string[]);
        }
        if (item.key === 'metadataColumns' && item.rawValue) {
          setValue('metadataColumns', item.rawValue as string[]);
        }
      });
    }
  }, [mdsaTaskInParams, setValue]);

  /* Loading indicator while fetching data */
  if (mdsaTaskParamsMetadataColumnsLoading) {
    return (
      <AppLayout screenTitle="Microbiome Downstream Analysis">
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginTop: 20,
          }}
        >
          <Spin size="large" />
        </div>
      </AppLayout>
    );
  }

  /* if any error occurred while fetching data will show error message */
  if (queryError) {
    return (
      <AppLayout screenTitle="Microbiome Downstream Analysis">
        <p style={{ color: 'red', textAlign: 'center' }}>{queryError.message}</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout screenTitle="Microbiome downstream analysis">
      <>
        <h2>Statistical Analysis - Beta Group Significance </h2>
        <EcFormItem
          label="Select distance matrices"
          name="distanceMatrices"
          helpText="Maximum of 2 matrices can be selected"
          control={control}
          errors={errors}
          isFieldRequired
          render={({ onChange, value }) => {
            return (
              <Checkbox.Group
                options={distanceMatrixOptionsForCheckbox}
                value={value as Array<string>}
                onChange={(checkedValue) => {
                  if (Array.isArray(checkedValue) && checkedValue.length > 2) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    message.error('Maximum of 2 matrices can be selected');
                    return;
                  }
                  onChange(checkedValue);
                }}
              />
            );
          }}
        />

        <div style={{ marginTop: 20 }}>
          <EcFormItem
            label="Select metadata column(s)"
            name="metadataColumns"
            helpText="Maximum of 3 columns can be selected"
            control={control}
            errors={errors}
            isFieldRequired
            render={({ onChange, value }) => {
              return (
                <Select
                  showSearch
                  value={value as Array<string>}
                  onChange={(val) => {
                    if (Array.isArray(val) && val.length > 3) {
                      // eslint-disable-next-line @typescript-eslint/no-floating-promises
                      message.error('Maximum of 3 columns can be selected');
                      return;
                    }
                    onChange(val);
                  }}
                  mode="multiple"
                  placeholder="Select a metadata column"
                  style={{ width: 300, marginTop: 5 }}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    filterSelectOptions(input, option as OptionProps)
                  }
                >
                  {Array.isArray(mdsaMetadataColumnsData) && mdsaMetadataColumnsData.length > 0
                    ? mdsaMetadataColumnsData.map((item) => (
                        <Select.Option value={item.column} key={item.column}>
                          {item.column}
                        </Select.Option>
                      ))
                    : null}
                </Select>
              );
            }}
          />
        </div>
        <p className={styles.highlightedInstruction}>
          The platform will run beta-group-significance for each selected matrix and column
          combination.
        </p>
        <Button
          onClick={handleSubmit(({ distanceMatrices, metadataColumns }) => {
            // obtains the initial value of task BetaGroupSignificance
            const defaultInParams = mdsaTasksInitialValues.find(
              (task) => task.key === Enum_Mdsa_Task_Key_Enum.BetaGroupSignificance,
            )?.in_params as Array<MdsaInParam>;

            // const to store immutable copy of in_params
            const taskInParamImmutableConst = JSON.parse(JSON.stringify(defaultInParams)) as Array<
              MdsaInParam
            >;

            /* Updating in_params data based on user input */
            /* eslint-disable no-param-reassign */
            taskInParamImmutableConst.forEach((item) => {
              if (item.key === 'distanceMatrices' && distanceMatrices) {
                item.enabled = true;
                item.rawValue = distanceMatrices;
              }
              if (item.key === 'metadataColumns' && metadataColumns) {
                item.enabled = true;
                item.rawValue = metadataColumns;
              }
            });

            handleUpdateAndStartMdsaTask(taskInParamImmutableConst);
          })}
          type="primary"
          loading={startAnalysisBtnLoading}
        >
          Start Analysis
        </Button>
      </>
    </AppLayout>
  );
};

export default MdsaBetaDiversity;
